import SunCalc from "suncalc"

// Rough UTC offset derived from longitude — accurate for most of the world
// where political timezones follow solar time (≤1 h from longitude/15 in 90%
// of inhabited areas). Known edge cases handled via the UI's +/- override:
//   — China: single UTC+8 zone spans 73°–135°E
//   — India: UTC+5:30 (non-integer)
//   — Spain: geographically UTC+0 but politically UTC+1
//   — DST transitions
export function estimateUtcOffsetHours(lng) {
  if (typeof lng !== "number" || !isFinite(lng)) return 0
  return Math.round(lng / 15)
}

// Converts a wall-clock local ISO string (as typed into <input type="datetime-local">)
// at a plot with a given UTC offset, to the absolute UTC Date that SunCalc needs.
//
// Why it's needed: `new Date("2026-06-22T12:00:00")` without a Z suffix is
// parsed in the BROWSER's timezone. For a plot in Almaty (UTC+6) viewed from
// Moscow (UTC+3), naively feeding that Date to SunCalc gives shadows from
// 09:00 UTC — i.e., 15:00 local to Almaty — three hours off from the
// "12:00" the user actually typed.
export function toUtcDate(isoLocal, utcOffsetHours = 0) {
  const d = new Date(isoLocal)
  if (isNaN(d.getTime())) return d
  const browserOffsetMin = -d.getTimezoneOffset()
  const plotOffsetMin = utcOffsetHours * 60
  // Shift UTC by the *difference* between browser and plot zones.
  return new Date(d.getTime() - (plotOffsetMin - browserOffsetMin) * 60_000)
}

// SunCalc convention (critical for getting shadows right the first time):
//   azimuth (radians) — direction along horizon, measured *from south,
//     rotating toward west*. So:
//       0      → sun due south
//       +π/2   → sun due west
//       -π/2   → sun due east
//       ±π     → sun due north
//   altitude (radians) — angle above horizon. Negative = below horizon.
//
// Our editor's local metres use x = east, y = south. Derivation in sun.md:
//   Sun horizontal direction (east, south) = (-sin(A),  cos(A))
//   Shadow direction (east, south)         = ( sin(A), -cos(A))

export function getSunPosition(date, lat, lng) {
  const pos = SunCalc.getPosition(date, lat, lng)
  return { azimuth: pos.azimuth, altitude: pos.altitude }
}

export function sunTimes(date, lat, lng) {
  return SunCalc.getTimes(date, lat, lng)
}

// Unit vector along shadow direction on the ground in (xM, yM) metres:
// shadow points away from the sun horizontally.
export function shadowDirection(azimuth) {
  return { dx: Math.sin(azimuth), dy: -Math.cos(azimuth) }
}

// Shadow length for an object standing `heightM` above ground.
// Returns null when sun is at or below horizon (no usable shadow).
export function shadowLengthMetres(altitude, heightM) {
  if (altitude <= 0 || !heightM || heightM <= 0) return null
  const len = heightM / Math.tan(altitude)
  // Cap extremely long shadows (very low sun) to keep rendering sane.
  return Math.min(len, heightM * 200)
}

// Day-of-year arc of sun positions for drawing a path on the plan.
// Samples hourly — enough for a smooth visual curve at plot scale.
// `isoLocalDay` is the wall-clock-at-plot date; offset converts to UTC.
//
// Known limitation: during a DST transition night in the browser's zone
// (e.g. 02:30 → 03:30 skip), one sample per year falls into the phantom
// hour and toUtcDate can be off by 1 h. The arc is a visual aid at
// 30-min granularity, so a single slightly misplaced sample is invisible.
export function sunArcSamples(isoLocalDay, lat, lng, utcOffsetHours = 0, stepMinutes = 60) {
  const samples = []
  const base = new Date(isoLocalDay)
  base.setHours(0, 0, 0, 0)
  for (let m = 0; m < 24 * 60; m += stepMinutes) {
    const iso = isoAtMinuteOfDay(base, m)
    const utc = toUtcDate(iso, utcOffsetHours)
    const pos = getSunPosition(utc, lat, lng)
    if (pos.altitude > 0) {
      samples.push({ date: utc, azimuth: pos.azimuth, altitude: pos.altitude, hour: m / 60 })
    }
  }
  return samples
}

function isoAtMinuteOfDay(day, minuteOfDay) {
  const d = new Date(day)
  d.setHours(Math.floor(minuteOfDay / 60), minuteOfDay % 60, 0, 0)
  const pad = (n) => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`
}
